import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import type { dtf } from "common/utils/models";
import { api } from "jiffy-api";

export function useDtfArtworksInfinite(params: dtf.getArtworkHistory.Request = {}) {
  const {
    search = "",
    sortOrder = "desc",
    artworkIds,
    artworksType,
    dtfChannelUuid,
    catalogNumber,
    perPage = 20,
    enhancementsStarted = false
  } = params;

  return useInfiniteQuery({
    enabled: !!dtfChannelUuid,
    queryKey: [
      "dtfArtworks",
      "infinite",
      {
        search,
        sortOrder,
        artworkIds,
        artworksType,
        dtfChannelUuid,
        catalogNumber,
        perPage,
        enhancementsStarted
      }
    ],
    queryFn: async ({ pageParam = 1 }): Promise<dtf.getArtworkHistory.Response> => {
      const apiParams = {
        per_page: perPage,
        page: pageParam,
        tab: artworksType,
        sort: `created_at_${sortOrder}`,
        artwork_ids: artworkIds || [],
        search,
        dtf_channel_uuid: dtfChannelUuid,
        catalog_number: catalogNumber,
        enhancement_started: enhancementsStarted
      };

      return api.dtf.getArtworkHistories(apiParams).then(objNamesToCamelCase);
    },
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = allPages.length;
      return currentPage < (lastPage.totalPages || 0) ? currentPage + 1 : undefined;
    },
    initialPageParam: 1
  });
}

export function useDtfArtworks(params: dtf.getArtworkHistory.Request = {}) {
  const {
    search = "",
    sortOrder = "desc",
    artworkIds,
    apiType = "list",
    artworksType,
    dtfChannelUuid,
    catalogNumber,
    perPage = 10,
    page = 1,
    enhancementsStarted = false
  } = params;

  return useQuery({
    enabled: !!dtfChannelUuid,
    queryKey: ["dtfArtworks", params],
    queryFn: async (): Promise<
      dtf.getArtworkHistory.Response | dtf.getArtworkHistoryDetails.Response
    > => {
      const apiParams = {
        per_page: perPage,
        page,
        tab: artworksType,
        sort: `created_at_${sortOrder}`,
        artwork_ids: artworkIds || [],
        search,
        dtf_channel_uuid: dtfChannelUuid,
        catalog_number: catalogNumber,
        enhancement_started: enhancementsStarted
      };

      if (apiType === "list") {
        return api.dtf.getArtworkHistories(apiParams).then(objNamesToCamelCase);
      }

      // apiType === "details"
      return api.dtf.getArtworkHistory(apiParams).then(objNamesToCamelCase);
    }
  });
}
